import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Loader2, Repeat } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAvailableCli, useChangeCli, useDialerCliList } from '../api';
import type { DialerCliRow } from '../types';
import { useModuleFilter } from '@/lib/permissions/use-module-filter';
import { MODULE_LISTING_KEYS, BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';

interface ChangeModalState {
  dialerId: number;
  allocatedCliId: number;
  dialerName: string;
  currentCli: string;
}

export function AllocateCliPage() {
  const dialerList = useDialerCliList();
  const filteredDialerList = useModuleFilter(dialerList.data ?? [], MODULE_LISTING_KEYS.MANAGE_DIALERS, (d) => d.dialerId);
  const change = useChangeCli();
  const [modal, setModal] = useState<ChangeModalState | null>(null);
  const [pickedCliId, setPickedCliId] = useState<string>('');

  const available = useAvailableCli(modal?.dialerId ?? null);

  const handleChange = () => {
    if (!modal) return;
    if (!pickedCliId) {
      toast.error('Pick a CLI to allocate.');
      return;
    }
    change.mutate(
      {
        cliId: pickedCliId,
        allocatedCliId: String(modal.allocatedCliId),
        dialerId: String(modal.dialerId),
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? `Allocated new CLI to ${modal.dialerName}`);
          setModal(null);
          setPickedCliId('');
        },
        onError: () => toast.error('Could not change CLI.'),
      },
    );
  };

  const columns = useMemo<ColumnDef<DialerCliRow>[]>(
    () => [
      {
        accessorKey: 'allocatedCliId',
        header: 'Allocated CLI ID',
        size: 140,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.allocatedCliId ?? '—'}
          </span>
        ),
      },
      { accessorKey: 'cli', header: 'CLI' },
      { accessorKey: 'dialerName', header: 'Dialer', size: 200 },
      {
        accessorKey: 'pilotNumber',
        header: 'Pilot No.',
        size: 140,
        cell: ({ row }) =>
          row.original.pilotNumber && Number(row.original.pilotNumber) !== 0
            ? row.original.pilotNumber
            : '—',
      },
      {
        id: 'actions',
        header: 'Settings',
        size: 130,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              setModal({
                dialerId: row.original.dialerId,
                allocatedCliId: row.original.allocatedCliId ?? 0,
                dialerName: row.original.dialerName,
                currentCli: row.original.cli ?? '',
              })
            }
          >
            <Repeat className="h-4 w-4" />
            Change CLI
          </Button>
        ),
      },
    ],
    [],
  );

  const canAllocate = usePermission('Manage CLI.Allocate CLI', BASE_PERMISSION.VIEW);

  if (!canAllocate) return <Navigate to="/cli-list" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Allocate CLI"
        description="Swap the active caller-line identity for each dialer."
        actions={
          <Button asChild variant="outline">
            <Link to="/cli-list">
              <ArrowLeft className="h-4 w-4" />
              Back to CLIs
            </Link>
          </Button>
        }
      />

      <DataTable<DialerCliRow, unknown>
        tableId="cli:allocate:v1"
        columns={columns}
        data={filteredDialerList}
        isLoading={dialerList.isLoading}
        searchPlaceholder="Search dialers…"
        emptyTitle="No dialers found"
        emptyDescription="Add a dialer first to allocate a CLI."
        getRowId={(row) => String(row.dialerId)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="dialer-cli.csv"
        pageSize={10}
      />

      <Dialog
        open={modal !== null}
        onOpenChange={(o) => {
          if (!o) {
            setModal(null);
            setPickedCliId('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change CLI</DialogTitle>
            <DialogDescription>
              {modal && (
                <>
                  Replace <span className="font-medium text-foreground">{modal.currentCli || '—'}</span>{' '}
                  on <span className="font-medium text-foreground">{modal.dialerName}</span> with a
                  different CLI.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cli-pick">New CLI</Label>
            <Select value={pickedCliId} onValueChange={setPickedCliId}>
              <SelectTrigger id="cli-pick">
                <SelectValue
                  placeholder={
                    available.isLoading ? 'Loading CLIs…' : 'Select a CLI'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(available.data ?? []).map((c) => (
                  <SelectItem key={c.cliId} value={String(c.cliId)}>
                    {c.cli}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setModal(null);
                setPickedCliId('');
              }}
              disabled={change.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleChange} disabled={change.isPending || !pickedCliId}>
              {change.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Change CLI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
}
